import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { AuthLayout, Field } from "./AuthLayout";
import { PremiumInput } from "../../components/brand/PremiumInput";
import { useAuth } from "../../data/authStore";
import { apiError } from "../../lib/api";

export default function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();

  const [form, setForm] = useState({ full_name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || !form.email.trim() || !form.password) {
      setError("All fields are required");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      const user = await register({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      toast.success(`Welcome to ChainFlow, ${user.full_name.split(" ")[0]}.`, { description: "Your workspace is ready." });
      nav("/today", { replace: true });
    } catch (e) {
      const msg = apiError(e);
      setError(msg);
      toast.error("Sign up failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      testid="signup-page"
      eyebrow="Get started"
      title="Create your ChainFlow workspace"
      subtitle="Spin up your operating system in under a minute. No card required to evaluate."
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-indigo-700 hover:text-indigo-800" data-testid="signup-to-login">Sign in →</Link></>}
    >
      <form onSubmit={onSubmit} data-testid="signup-form" noValidate>
        <Field label="Full name">
          <PremiumInput
            type="text"
            autoComplete="name"
            size="lg"
            value={form.full_name}
            onChange={(v) => set("full_name", v)}
            placeholder="Olivia Marsh"
            testid="signup-full-name"
            autoFocus
          />
        </Field>
        <Field label="Work email">
          <PremiumInput
            type="email"
            autoComplete="email"
            size="lg"
            value={form.email}
            onChange={(v) => set("email", v)}
            placeholder="you@agency.co.uk"
            testid="signup-email"
          />
        </Field>
        <Field label="Password">
          <PremiumInput
            type="password"
            autoComplete="new-password"
            size="lg"
            value={form.password}
            onChange={(v) => set("password", v)}
            placeholder="At least 8 characters"
            testid="signup-password"
          />
        </Field>
        {error && <p className="text-[12px] text-rose-600 -mt-1 mb-3" data-testid="signup-error">{error}</p>}
        <p className="text-[11px] text-slate-500 mb-4 leading-snug">
          By creating an account you agree to ChainFlow's terms of service and privacy policy.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className={`btn btn-primary !h-11 !px-5 !text-[13.5px] w-full ${submitting ? "opacity-60 cursor-not-allowed !shadow-none" : ""}`}
          data-testid="signup-submit"
        >
          {submitting ? <><Loader2 size={14} className="animate-spin" /> Creating workspace…</> : <>Create workspace <ArrowRight size={14} /></>}
        </button>
      </form>
    </AuthLayout>
  );
}
